import { useCallback, useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  BottomNavigation,
  BottomNavigationAction,
  CircularProgress,
  Typography,
} from '@mui/material';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import AddCardRoundedIcon from '@mui/icons-material/AddCardRounded';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import PhoneAndroidRoundedIcon from '@mui/icons-material/PhoneAndroidRounded';
import ContactSupportOutlinedIcon from '@mui/icons-material/ContactSupportOutlined';
import { walletApi } from '../../../services/api/walletApi';
import '../accountTheme.css';
import './AccountScreen.css';

const TX_LABELS = {
  ADMIN_CREDIT: 'Admin credit',
  ADMIN_DEBIT: 'Admin debit',
  DEPOSIT: 'Deposit',
  DEPOSIT_BONUS: 'Bonus',
  WITHDRAW: 'Withdrawal',
  GAME_BET: 'Game bet',
  GAME_WIN: 'Game win',
  GAME_REFUND: 'Refund',
};

function getTxLabel(tx) {
  if (tx.note && ['DEPOSIT', 'DEPOSIT_BONUS', 'ADMIN_CREDIT'].includes(tx.type)) {
    return tx.note;
  }
  return TX_LABELS[tx.type] || tx.type;
}

function formatTxAmount(type, amount) {
  const credit = ['ADMIN_CREDIT', 'DEPOSIT', 'DEPOSIT_BONUS', 'GAME_WIN', 'GAME_REFUND'].includes(type);
  const prefix = credit ? '+' : '-';
  return `${prefix}₹${Math.abs(amount).toFixed(2)}`;
}

function AccountScreen({
  user,
  onOpenHome,
  onOpenWallet,
  onOpenWithdraw,
  onOpenDeposit,
  onOpenContact,
  onLogout,
}) {
  const [transactions, setTransactions] = useState([]);
  const [loadingTx, setLoadingTx] = useState(true);

  const loadTransactions = useCallback(async () => {
    try {
      const data = await walletApi.transactions();
      setTransactions(data.transactions?.slice(0, 8) ?? []);
    } catch {
      setTransactions([]);
    } finally {
      setLoadingTx(false);
    }
  }, []);

  useEffect(() => {
    loadTransactions();
  }, [loadTransactions]);

  const balance = user?.walletBalance ?? 0;
  const initials = (user?.name || 'P').charAt(0).toUpperCase();

  return (
    <Box className="bz-page account-page">
      <Box className="bz-topbar account-topbar">
        <Typography className="bz-topbar__title">My Account</Typography>
        <Box sx={{ width: 32 }} />
      </Box>

      <Box className="bz-scroll">
        <Box className="account-profile">
          <Avatar className="account-avatar">{initials}</Avatar>
          <Box>
            <Typography className="account-name">{user?.name || 'Player'}</Typography>
            <Typography className="account-phone">
              <PhoneAndroidRoundedIcon sx={{ fontSize: 14 }} />
              {user?.phone || user?.email || '—'}
            </Typography>
          </Box>
        </Box>

        <Box className="bz-balance-hero">
          <Typography className="bz-balance-label">Available balance</Typography>
          <Typography className="bz-balance-value">
            ₹{balance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
          </Typography>
        </Box>

        <Box className="bz-actions-row">
          <button type="button" className="bz-action-btn bz-action-btn--deposit" onClick={onOpenDeposit}>
            <span className="bz-action-btn__icon"><AddCardRoundedIcon /></span>
            <span className="bz-action-btn__label">Deposit</span>
          </button>
          <button type="button" className="bz-action-btn bz-action-btn--withdraw" onClick={onOpenWithdraw}>
            <span className="bz-action-btn__icon"><AccountBalanceWalletRoundedIcon /></span>
            <span className="bz-action-btn__label">Withdraw</span>
          </button>
        </Box>

        <button type="button" className="account-wallet-link" onClick={onOpenWallet}>
          <ReceiptLongRoundedIcon sx={{ fontSize: 20 }} />
          <span>View full wallet & history</span>
          <span className="account-wallet-link__arrow">→</span>
        </button>

        <button type="button" className="account-wallet-link" onClick={onOpenContact}>
          <ContactSupportOutlinedIcon sx={{ fontSize: 20 }} />
          <span>Contact us</span>
          <span className="account-wallet-link__arrow">→</span>
        </button>

        <Typography className="bz-section-title">Recent transactions</Typography>
        <Box className="bz-card">
          {loadingTx ? (
            <Box className="bz-empty"><CircularProgress size={28} sx={{ color: '#ff6b6b' }} /></Box>
          ) : transactions.length === 0 ? (
            <Typography className="bz-empty">No transactions yet — play a game to get started</Typography>
          ) : (
            transactions.map((tx) => (
              <Box key={tx.id} className="bz-tx-row">
                <Box>
                  <Typography className="bz-tx-type">{getTxLabel(tx)}</Typography>
                  <Typography className="bz-tx-note">
                    {tx.note && !['DEPOSIT', 'DEPOSIT_BONUS', 'ADMIN_CREDIT'].includes(tx.type)
                      ? tx.note
                      : new Date(tx.createdAt).toLocaleString('en-IN')}
                  </Typography>
                </Box>
                <Typography
                  className={
                    ['ADMIN_CREDIT', 'DEPOSIT', 'DEPOSIT_BONUS', 'GAME_WIN', 'GAME_REFUND'].includes(tx.type)
                      ? 'bz-tx-amount--credit'
                      : 'bz-tx-amount--debit'
                  }
                >
                  {formatTxAmount(tx.type, tx.amount)}
                </Typography>
              </Box>
            ))
          )}
        </Box>

        <button type="button" className="bz-logout-btn" onClick={onLogout}>
          <LogoutRoundedIcon sx={{ fontSize: 20 }} />
          Log out
        </button>
      </Box>

      <nav className="bz-bottom-nav">
        <BottomNavigation showLabels value={1}>
          <BottomNavigationAction label="Games" icon={<HomeOutlinedIcon />} onClick={onOpenHome} />
          <BottomNavigationAction label="Account" icon={<AccountCircleOutlinedIcon />} />
        </BottomNavigation>
      </nav>
    </Box>
  );
}

export default AccountScreen;
